import React, { useState } from 'react'

import { Box, Text, theme, useBreakpoint } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0AvatarModule } from '@factorialco/f0-react'
import { Cross } from '@factorialco/f0-react/icons/app'
import { InAppCommsSidebarCard, Maybe } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import Media from 'modules/in_app_comms/components/Media'
import { moduleAvailableToModuleId } from 'modules/upsell/lib/moduleIcons'

interface SidebarCardProps {
  content: Maybe<InAppCommsSidebarCard> | undefined
  componentName: string
  onDismiss: () => void
}

const SidebarCard = ({ content, componentName, onDismiss }: SidebarCardProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const { isMobile } = useBreakpoint()
  const showCloseButton = isMobile || isHovered

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onDismiss()
    track(trackingEvents.inAppComms.dismissInAppCommsComponent, {
      component: 'in_app_comms_sidebar_card',
      componentName,
    })
  }

  const triggerEvent = (action: string) => {
    track(trackingEvents.inAppComms.clickInAppCommsComponent, {
      componentType: 'in_app_comms_sidebar_card',
      componentName,
      buttonAction: action,
    })
  }

  useEffect(() => {
    if (!content?.title) return
    track(trackingEvents.inAppComms.viewInAppCommsComponent, {
      component: 'in_app_comms_sidebar_card',
      componentName,
    })
  }, [content?.title, componentName])

  if (!content || (!content.media?.[0]?.url && !content.moduleIcon)) {
    return null
  }

  return (
    <Box
      borderRadius={{ all: 'abs016' }}
      position="fixed"
      style={{
        bottom: isMobile ? theme.dimensions.s12 : theme.dimensions.s64,
        left: theme.dimensions.s12,
        zIndex: theme.zIndex.modals,
        maxWidth: '216px',
        width: '100%',
        boxShadow: theme.shadows.s50,
      }}
    >
      <Box
        background={isHovered ? 'grey100' : 'grey000White'}
        borderRadius={{ all: 'abs016' }}
        position="relative"
        style={{
          overflow: 'hidden',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          triggerEvent(`Open link: ${content.cardLink?.link}`)
          if (content.cardLink?.openInNewTab) {
            window.open(content.cardLink?.link || '', '_blank')
          } else {
            window.location.href = content.cardLink?.link || ''
          }
          onDismiss()
        }}
      >
        <Box
          position="absolute"
          style={{
            top: '8px',
            right: '8px',
            zIndex: theme.zIndex.modals + 1,
            opacity: showCloseButton ? '1' : '0',
            transition: 'opacity 0.2s ease-in-out',
          }}
        >
          <F0Button
            onClick={handleClose}
            variant="outline"
            size="sm"
            icon={Cross}
            hideLabel
            round
            label="Close"
            style={{
              backgroundColor: theme.colors.grey000White,
              borderRadius: theme.dimensions.s8,
              opacity: '0.7',
            }}
          />
        </Box>
        {!content.moduleIcon && <Media content={content} />}
        <Box padding="s12" gap="s12">
          {content.moduleIcon && (
            <F0AvatarModule module={moduleAvailableToModuleId(content.moduleIcon)} size="lg" />
          )}
          <Box gap="s4">
            <Text.Normal size="200" weight="semibold" ellipsis lineClamp={2} color="grey1000">
              {content.title}
            </Text.Normal>
            <Text.Normal size="200" ellipsis lineClamp={3}>
              {content.description}
            </Text.Normal>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default SidebarCard
